import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  CheckCircle, Download, FileText, Loader2, AlertCircle, ChevronDown, 
  Sparkles, Clock, FileCheck
} from "lucide-react";
import { API, LOGO_URL, packageNames } from "../common/constants";

// Executive template options
const executiveTemplates = [
  { id: "executive_teal", name: "Executive Teal", color: "#0D9488" },
  { id: "executive_navy", name: "Executive Navy", color: "#1E3A5F" },
  { id: "executive_charcoal", name: "Executive Charcoal", color: "#374151" },
  { id: "executive_burgundy", name: "Executive Burgundy", color: "#7C2D12" },
];

// Generation steps for progress display
const generationSteps = [
  { id: "analyzing", label: "Analyzing your resume", duration: 5 },
  { id: "optimizing", label: "Optimizing content for ATS", duration: 15 },
  { id: "generating", label: "Generating tailored documents", duration: 30 },
  { id: "formatting", label: "Applying professional formatting", duration: 10 },
  { id: "finalizing", label: "Finalizing your package", duration: 5 },
];

const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [session, setSession] = useState(null);
  const [downloading, setDownloading] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState("executive_teal");
  const [showTemplates, setShowTemplates] = useState(false);
  const [generationStatus, setGenerationStatus] = useState("checking");
  const [generationProgress, setGenerationProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [packageType, setPackageType] = useState("basic");

  useEffect(() => {
    const checkPayment = async () => {
      const checkoutSessionId = searchParams.get("session_id");
      
      if (!checkoutSessionId) {
        setStatus("error");
        return;
      }

      try {
        // This endpoint now triggers document generation if payment is confirmed
        const statusResponse = await axios.get(`${API}/payment-status/${checkoutSessionId}`);
        
        if (statusResponse.data.payment_status === "paid") {
          const genStatus = statusResponse.data.generation_status;
          
          if (genStatus === "complete" && statusResponse.data.session_id) {
            setStatus("success");
            setGenerationStatus("complete");
            setGenerationProgress(100);
            
            // Fetch the generated session
            const sessionResponse = await axios.get(`${API}/session/${statusResponse.data.session_id}`);
            setSession(sessionResponse.data);
            setPackageType(sessionResponse.data.package_type || "basic");
          } else if (genStatus === "failed" || genStatus === "error") {
            setStatus("error");
            setGenerationStatus("error");
            console.error("Generation error:", statusResponse.data.error);
          } else if (genStatus === "generating" || genStatus === "queued") {
            // Payment confirmed, documents generating...
            setStatus("generating");
            setGenerationStatus(genStatus);
          } else {
            // Still processing
            setStatus("generating");
            setGenerationStatus("processing");
          }
        } else {
          setStatus("pending");
        }
      } catch (error) {
        console.error("Error checking payment:", error);
        setStatus("error");
      }
    };

    checkPayment();
    
    // Poll more frequently during generation
    const interval = setInterval(() => {
      if (status === "pending" || status === "generating") {
        checkPayment();
      }
    }, status === "generating" ? 3000 : 5000);

    return () => clearInterval(interval);
  }, [searchParams, status]);

  // Progress animation effect for generating state
  useEffect(() => {
    if (status !== "generating") return;

    // Calculate expected duration based on package type
    const isPremium = packageType === "premium" || packageType === "complete" || packageType === "career_profiler";
    const totalDuration = isPremium ? 90 : 30; // seconds
    
    const progressInterval = setInterval(() => {
      setElapsedTime(prev => {
        const newTime = prev + 1;
        // Calculate progress (cap at 95% until complete)
        const progress = Math.min(95, (newTime / totalDuration) * 100);
        setGenerationProgress(progress);
        
        // Update current step based on progress
        if (progress < 10) setCurrentStep(0);
        else if (progress < 30) setCurrentStep(1);
        else if (progress < 60) setCurrentStep(2);
        else if (progress < 85) setCurrentStep(3);
        else setCurrentStep(4);
        
        return newTime;
      });
    }, 1000);

    return () => clearInterval(progressInterval);
  }, [status, packageType]);

  const handleDownload = async (docType, format) => {
    if (!session?.session_id) {
      toast.error("Session not found. Please contact support.");
      return;
    }

    setDownloading(prev => ({ ...prev, [`${docType}-${format}`]: true }));
    
    try {
      const templateParam = selectedTemplate ? `?template=${selectedTemplate}` : '';
      const response = await axios.get(
        `${API}/download/${session.session_id}/${docType}/${format}${templateParam}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const docNames = {
        resume: "Resume",
        cover_letter: "Cover_Letter",
        cv: "CV",
        interview_guide: "Interview_Guide",
        career_narrative: "Career_Narrative",
        job_recommendations: "Job_Strategy"
      };
      
      const userName = session.user_name?.replace(/\s+/g, '_') || "Document";
      link.setAttribute('download', `${userName}_${docNames[docType]}.${format}`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${docNames[docType]} downloaded!`);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Download failed. Please try again or contact support.");
    } finally {
      setDownloading(prev => ({ ...prev, [`${docType}-${format}`]: false }));
    }
  };

  const getAvailableDocuments = () => {
    if (!session) return [];
    
    const packageType = session.package_type || "basic";
    const packageMapping = { "complete": "premium" };
    const normalizedPackage = packageMapping[packageType] || packageType;
    
    const docs = [
      { type: "resume", name: "Resume", icon: FileText, always: true },
      { type: "cover_letter", name: "Cover Letter", icon: FileText, always: true },
    ];
    
    if (normalizedPackage === "premium" || normalizedPackage === "career_profiler") {
      docs.push({ type: "cv", name: "Professional CV", icon: FileText });
      docs.push({ type: "interview_guide", name: "Interview Guide", icon: FileText });
    }
    
    if (normalizedPackage === "career_profiler") {
      docs.push({ type: "career_narrative", name: "Career Narrative", icon: FileText });
      docs.push({ type: "job_recommendations", name: "Job Strategy Guide", icon: FileText });
    }
    
    return docs;
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-teal-400 mx-auto" />
          <p className="text-muted">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto" />
            <h2 className="text-xl font-semibold">Something went wrong</h2>
            <p className="text-muted">We couldn't verify your payment. Please contact support at aaron@aiquires.com</p>
            <Link to="/resume">
              <Button>Back to Resume Builder</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === "pending") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <Loader2 className="w-12 h-12 animate-spin text-amber-400 mx-auto" />
            <h2 className="text-xl font-semibold">Payment Processing</h2>
            <p className="text-muted">Your payment is being processed. This page will update automatically.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === "generating") {
    const isPremium = packageType === "premium" || packageType === "complete" || packageType === "career_profiler";
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-background/95 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full border-[#00a3e0]/20 shadow-xl">
          <CardContent className="pt-8 pb-8 space-y-6">
            {/* Animated Icon */}
            <div className="relative w-24 h-24 mx-auto">
              <div className="absolute inset-0 bg-[#00a3e0]/20 rounded-full animate-ping" />
              <div className="absolute inset-2 bg-[#00a3e0]/10 rounded-full animate-pulse" />
              <div className="absolute inset-0 flex items-center justify-center">
                <Sparkles className="w-12 h-12 text-[#00a3e0] animate-pulse" />
              </div>
            </div>
            
            {/* Title */}
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-[#071734]">Creating Your Documents</h2>
              <p className="text-gray-600">
                {isPremium 
                  ? "Building your complete career package with Resume, CV, and Interview Guide..."
                  : "Crafting your ATS-optimized Resume and Cover Letter..."}
              </p>
            </div>
            
            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Progress</span>
                <span className="font-medium text-[#00a3e0]">{Math.round(generationProgress)}%</span>
              </div>
              <Progress value={generationProgress} className="h-3 bg-gray-100" />
            </div>
            
            {/* Current Step */}
            <div className="bg-[#00a3e0]/5 rounded-lg p-4 border border-[#00a3e0]/20">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[#00a3e0]/20 rounded-full flex items-center justify-center">
                  <Loader2 className="w-4 h-4 text-[#00a3e0] animate-spin" />
                </div>
                <div>
                  <p className="font-medium text-[#071734]">{generationSteps[currentStep]?.label}</p>
                  <p className="text-xs text-gray-500">Step {currentStep + 1} of {generationSteps.length}</p>
                </div>
              </div>
            </div>
            
            {/* Steps List */}
            <div className="space-y-2">
              {generationSteps.map((step, index) => (
                <div 
                  key={step.id}
                  className={`flex items-center gap-3 text-sm transition-all duration-300 ${
                    index < currentStep 
                      ? 'text-green-600' 
                      : index === currentStep 
                        ? 'text-[#00a3e0] font-medium' 
                        : 'text-gray-400'
                  }`}
                >
                  {index < currentStep ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : index === currentStep ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Clock className="w-4 h-4" />
                  )}
                  <span>{step.label}</span>
                </div>
              ))}
            </div>
            
            {/* Time Estimate */}
            <div className="text-center">
              <p className="text-xs text-gray-500">
                <Clock className="w-3 h-3 inline mr-1" />
                {isPremium 
                  ? "Premium packages typically take 60-90 seconds" 
                  : "Usually completes in 15-30 seconds"}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Elapsed: {elapsedTime}s
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const documents = getAvailableDocuments();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="container-main flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="AiQ Phoenix Tech" className="h-10 w-auto rounded-md" />
          </Link>
          <Badge variant="outline" className="border-green-500/50 text-green-400">
            <CheckCircle className="w-3 h-3 mr-1" /> Payment Complete
          </Badge>
        </div>
      </header>

      <main className="container-main py-12">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Success Message */}
          <div className="text-center space-y-4">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h1 className="text-3xl font-heading font-bold">Thank You!</h1>
            <p className="text-muted text-lg">
              Your {packageNames[session?.package_type] || "Complete"} package is ready to download.
            </p>
          </div>

          {/* Template Selector */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Choose Your Template Style</CardTitle>
              <CardDescription>Select a premium executive template for your Resume and Cover Letter</CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="outline"
                className="w-full justify-between"
                onClick={() => setShowTemplates(!showTemplates)}
              >
                <div className="flex items-center gap-3">
                  <div 
                    className="w-6 h-6 rounded-full border-2" 
                    style={{ backgroundColor: executiveTemplates.find(t => t.id === selectedTemplate)?.color }}
                  />
                  <span>{executiveTemplates.find(t => t.id === selectedTemplate)?.name}</span>
                </div>
                <ChevronDown className={`w-4 h-4 transition-transform ${showTemplates ? 'rotate-180' : ''}`} />
              </Button>
              
              {showTemplates && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
                  {executiveTemplates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => { setSelectedTemplate(template.id); setShowTemplates(false); }}
                      className={`p-3 rounded-lg border text-center transition-all ${
                        selectedTemplate === template.id 
                          ? 'border-teal-500 bg-teal-500/10' 
                          : 'border-border hover:border-teal-500/50'
                      }`}
                    >
                      <div 
                        className="w-8 h-8 rounded-full mx-auto mb-2 border-2"
                        style={{ backgroundColor: template.color }}
                      />
                      <span className="text-xs">{template.name.replace('Executive ', '')}</span>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Download Section */}
          <Card>
            <CardHeader>
              <CardTitle>Your Documents</CardTitle>
              <CardDescription>Click to download in your preferred format</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {documents.map((doc) => (
                <div 
                  key={doc.type}
                  className="flex items-center justify-between p-4 bg-surface rounded-lg border border-border"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-teal-500/10 rounded-lg flex items-center justify-center">
                      <doc.icon className="w-5 h-5 text-teal-400" />
                    </div>
                    <div>
                      <h3 className="font-medium">{doc.name}</h3>
                      <p className="text-xs text-muted">Microsoft Word format</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(doc.type, "docx")}
                      disabled={downloading[`${doc.type}-docx`]}
                      data-testid={`download-${doc.type}-docx`}
                    >
                      {downloading[`${doc.type}-docx`] ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-1" /> Download DOCX
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Support Card */}
          <Card className="border-teal-500/20">
            <CardContent className="pt-6 text-center space-y-4">
              <h3 className="font-heading font-semibold">Need Help?</h3>
              <p className="text-sm text-muted">
                If you have any questions or need assistance, please contact us at{" "}
                <a href="mailto:aaron@aiquires.com" className="text-teal-400 hover:underline">
                  aaron@aiquires.com
                </a>
              </p>
              <Link to="/">
                <Button variant="outline" className="mt-4">
                  Back to Home
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default SuccessPage;
